"use client"

/**
 * @fileoverview Thin shim for category context.
 *
 * Previously this context seeded categories from hardcoded static data.
 * It now delegates entirely to the real API-backed hook from `features/categories`.
 *
 * All call-sites that import `useCategories` or `CategoryProvider` from this
 * module will transparently use live database data going forward.
 */

import { useCategoriesQuery, useCreateCategory, useUpdateCategory, useDeleteCategory } from "@/features/categories"
import type { TCategory, TCategoryCreate, TCategoryUpdate } from "@/features/categories"
import type { ReactNode } from "react"

// ─── Re-export the canonical type so call-sites don't need to update imports ──

export type { TCategory }

// ─── Context value shape (kept identical to the old context for compatibility) ─

type TCategoryContextValue = {
  categories: TCategory[]
  isLoading: boolean
  addCategory: (category: Omit<TCategory, "id" | "userId" | "isSystem" | "createdAt" | "updatedAt">) => void
  updateCategory: (category: TCategoryUpdate) => void
  deleteCategory: (id: string) => void
}

// ─── No-op provider — kept so existing <CategoryProvider> usages compile ──────

type TProps = {
  children: ReactNode
}

/**
 * CategoryProvider no longer manages any local state.
 * It is kept as a passthrough so existing provider-tree usage compiles without
 * requiring every import to be updated in one sweep.
 */
export function CategoryProvider({ children }: TProps) {
  return <>{children}</>
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Returns API-backed categories and mutation helpers.
 * Replaces the old hardcoded-context version.
 */
export function useCategories(): TCategoryContextValue {
  const { data: categories = [], isLoading } = useCategoriesQuery()
  const createMutation = useCreateCategory()
  const updateMutation = useUpdateCategory()
  const deleteMutation = useDeleteCategory()

  function addCategory(data: Omit<TCategory, "id" | "userId" | "isSystem" | "createdAt" | "updatedAt">): void {
    const payload: TCategoryCreate = {
      name: data.name,
      color: data.color,
      icon: data.icon,
    }
    createMutation.mutate(payload)
  }

  function updateCategory(data: TCategoryUpdate): void {
    updateMutation.mutate(data)
  }

  function deleteCategory(id: string): void {
    deleteMutation.mutate(id)
  }

  return {
    categories,
    isLoading,
    addCategory,
    updateCategory,
    deleteCategory,
  }
}
